import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Nav from "./Navigation.js";
import NavigationCard from "./NavigationCard.js";
import { getTokenInternal } from "./useToken";
import React from "react";
import {
  LogIn,
  UserPlus,
  ListChecks,
  BarChart3,
  Puzzle,
  Image,
  Lightbulb,
  Shuffle,
  Layers,
  Ghost,
  Map,
  Music,
  MessageCircle,
  KeyRound,
  MessagesSquare,
  Gamepad2,
  Users,
  X,
} from "lucide-react";

function Reveal({ children, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
    >
      {children}
    </div>
  );
}

export default function Main() {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState(null);
  const [games, setGames] = useState([]);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    getTokenInternal().then(setToken);
  }, [location]);

  useEffect(() => {
    if (!location.state?.notice) return;
    setNotice(location.state.notice);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location, navigate]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_HOST}/api/games`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setGames)
      .catch(() => setGames([]));
  }, []);

  const go = (path, external) =>
    external ? window.open(path, "_blank") : navigate(path);

  const primaryActions = [
    { label: "Log In", path: "/login", icon: LogIn },
    { label: "Sign Up", path: "/signup", icon: UserPlus },
  ];

  const trackerSection = token
    ? {
        title: "My Personal Trackers",
        items: [
          ...games.map((g) => ({
            label: `My Tracker: ${g.name}`,
            path: `/myitems/${g.name}`,
            icon: Gamepad2,
          })),
          { label: "Friend's Trackers", path: "/friendlist", icon: Users },
        ],
      }
    : {
        title: "Item Tracking",
        items: [{ label: "View List of All Items", path: "/Items", icon: ListChecks }],
      };

  const sections = [
    trackerSection,
    {
      title: "Puzzles & Tools",
      groups: [
        {
          subtitle: "Practice",
          items: [
            { label: "Lights Out Practice", path: "/LightsOut", icon: Lightbulb },
            { label: "Slider Game Practice", path: "/SliderGame", icon: Shuffle },
            { label: "Key Puzzle Practice", path: "/rush-hour/game", icon: KeyRound },
          ],
        },
        {
          subtitle: "Solvers",
          items: [
            { label: "Lights Out Solver", path: "/LightsOutSolver", icon: Lightbulb },
            { label: "Slider Solver", path: "/slider/solver", icon: Shuffle },
            { label: "Key Puzzle Solver", path: "/rush-hour/solver", icon: KeyRound },
          ],
        },
        {
          subtitle: "Other Tools",
          items: [
            { label: "Painting Tracker", path: "/paintingSolver", icon: Image },
            { label: "Matching Puzzle Helper", path: "/memory", icon: Layers },
          ],
        },
      ],
    },
    {
      title: "Reference & Community",
      items: [
        ...(token ? [{ label: "View List of All Items", path: "/Items", icon: ListChecks }] : []),
        { label: "All Item Statistics", path: "/statistics", icon: BarChart3 },
        { label: "Full List of Puzzles", path: "/puzzles", icon: Puzzle },
        { label: "Meet the Monsters", path: "/monsters", icon: Ghost },
        { label: "Collection of Maps (WIP)", path: "/locations", icon: Map },
        {
          label: "Labyrinthine OST",
          path: "https://www.youtube.com/watch?v=HamKDYBTsMo&list=PLr-PNnROr79txSszTZNqae3uH1F5yRisI",
          icon: Music,
          external: true,
        },
        {
          label: "Join Valko Discord",
          path: "https://discord.gg/uBvZnu5Hc",
          icon: MessagesSquare,
          external: true,
        },
        { label: "Have Input? Contact Me", path: "/suggestions/submit", icon: MessageCircle },
      ],
    },
  ];

  return (
    <div className="relative min-h-screen">
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 600px at 82% -8%, rgba(147,51,234,0.16), transparent 60%), " +
            "radial-gradient(1000px 700px at 8% 110%, rgba(88,28,135,0.22), transparent 60%), " +
            "#000",
        }}
      />
      <Nav />
      <div className="flex justify-center">
        <div className="w-full max-w-4xl mx-auto text-white p-4">
          {notice && (
            <div className="flex items-center justify-between gap-3 bg-purple-950/40 border border-purple-700/60 text-purple-100 text-sm rounded-lg px-4 py-2.5 mb-4">
              <span>{notice}</span>
              <button
                onClick={() => setNotice(null)}
                aria-label="Dismiss"
                className="text-purple-300 hover:text-white transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          )}
          <div className="text-center font-primary text-2xl sm:text-3xl md:text-4xl font-bold mb-2 mt-2">
            Welcome to the Unofficial Labyrinthine Tracker
          </div>
          <div className="text-center text-gray-400 text-base sm:text-lg mb-3">
            Have a suggestion, concern, or issue? Use "Contact Me" below.
          </div>

          {/* Primary actions -- only for logged-out visitors */}
          {!token && (
            <div className="flex items-center justify-center gap-4 mb-4">
              <img
                src={require("../images/ClariceIcon.png")}
                alt="Clarice"
                className="hidden sm:block h-16 w-16 drop-shadow-lg"
              />
              {primaryActions.map(({ label, path, icon: Icon }) => (
                <button
                  key={path}
                  onClick={() => go(path)}
                  className="flex items-center gap-2 bg-purple-700 hover:bg-purple-600 transition-colors px-5 py-2.5 rounded-lg font-semibold text-base"
                >
                  <Icon size={20} />
                  {label}
                </button>
              ))}
              <img
                src={require("../images/ClariceIcon.png")}
                alt="Clarice"
                className="hidden sm:block h-16 w-16 drop-shadow-lg"
              />
            </div>
          )}

          {/* Grouped sections */}
          {sections.map(({ title, items, groups }) => (
            <Reveal key={title} className="mb-3">
              <h2 className="font-primary text-xl sm:text-xl md:text-2xl lg:text-2xl font-bold text-purple-300 mb-1.5 border-b border-purple-900 pb-1">
                {title}
              </h2>

              {items && (
                <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
                  {items.map(({ label, path, icon, external }) => (
                    <NavigationCard
                      key={path}
                      label={label}
                      icon={icon}
                      onClick={() => go(path, external)}
                    />
                  ))}
                </div>
              )}

              {groups &&
                groups.map(({ subtitle, items: groupItems }) => (
                  <div key={subtitle} className="mb-4 last:mb-0">
                    <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wide">
                      {subtitle}
                    </h3>
                    <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
                      {groupItems.map(({ label, path, icon, external }) => (
                        <NavigationCard
                          key={path}
                          label={label}
                          icon={icon}
                          onClick={() => go(path, external)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
